import { useEffect, useState } from 'react';
import { useVgpsRuntime } from 'livekit_web/react';
import type { VgpsPlaybackSession } from 'livekit_web';
import { leasePlaybackSession } from '../session_registry';

export interface UsePlaybackSessionParams {
  deviceSerial: string;
  channelId: string;
  startTsMs: number;
  endTsMs: number;
  qualityHint?: 'hd' | 'sd' | 'auto';
}

export interface UsePlaybackSessionResult {
  session: VgpsPlaybackSession | null;
  error: Error | null;
  ready: boolean;
}

export function usePlaybackSession({
  deviceSerial,
  channelId,
  startTsMs,
  endTsMs,
  qualityHint,
}: UsePlaybackSessionParams): UsePlaybackSessionResult {
  const runtime = useVgpsRuntime();
  const [session, setSession] = useState<VgpsPlaybackSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!runtime || !deviceSerial || !channelId || !startTsMs || !endTsMs) {
      setSession(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let releaseRef: (() => Promise<void>) | null = null;

    setSession(null);
    setError(null);

    void (async () => {
      try {
        const lease = await leasePlaybackSession(runtime, {
          deviceSerial,
          channelId,
          startTsMs,
          endTsMs,
          qualityHint,
        });
        releaseRef = lease.release;

        if (cancelled) {
          await lease.release();
          return;
        }

        setSession(lease.session);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();

    return () => {
      cancelled = true;
      setSession(null);
      if (releaseRef) void releaseRef();
    };
  }, [runtime, deviceSerial, channelId, startTsMs, endTsMs, qualityHint]);

  return { session, error, ready: session !== null };
}
